import { snakeCase } from 'lodash';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, AppDispatch, getClient, validationErrors } from '@src/core/store';
import {
  Cart,
  CartItem,
  AddCartItemRequest,
  ErrorResponse,
  AddProductPackageToCartRequest,
} from '@src/core/buyerApiClient';
import { AxiosError, AxiosResponse } from 'axios';
import { loadSaleOrigin, loadAffiliationCode } from '@src/common';
import * as storage from '@src/common/storage';
import { setProductAdderStatus } from '@src/features/product';
import { publicApiTokenSelector } from '@src/features/auth';

const planneWidgetCartKey = 'planneWidgetCart';

export interface CartState {
  cart?: Cart;
  cartItemAdded?: boolean;
  cartItemAddedErrors?: { [key: string]: string };
  couponErrors?: CouponErrorsType[];
  isModalOpened: boolean;
  isLoadingCart: boolean;
  isLoadingCoupon: boolean;
}

export const CouponErrorEnum = {
  invalidValue: 'invalid_value',
  couponIsActive: 'coupon_is_active',
  couponHasUsesLeft: 'coupon_has_uses_left',
  couponHasCustomerUsesLeft: 'coupon_has_customer_uses_left',
  couponHasSomeProducts: 'coupon_has_some_products',
  couponPurchaseMinAmountCentsNotReached: 'coupon_purchase_min_amount_cents_not_reached',
  couponHasSomeValidItem: 'coupon_has_some_valid_item',
};

export type CouponErrorsType = (typeof CouponErrorEnum)[keyof typeof CouponErrorEnum];

const initialState: CartState = {
  isModalOpened: false,
  isLoadingCart: true,
  isLoadingCoupon: false,
};

interface CartAddResponse {
  ok: boolean;
  errors?: { [key: string]: string[] };
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCart: (state: CartState, action: PayloadAction<Cart, string>) => {
      state.cart = action.payload;
    },
    requestCartItemAdded: (state: CartState) => {
      state.cartItemAdded = undefined;
      state.cartItemAddedErrors = undefined;
    },
    receiveCartItemAdded: (state: CartState, { payload: { ok, errors } }: PayloadAction<CartAddResponse, string>) => {
      state.cartItemAdded = ok;

      if (errors) {
        state.cartItemAddedErrors = Object.keys(errors).reduce(
          (carry, key) => ({ ...carry, [key]: errors[key]!.join(', ') }),
          {},
        );
      }
    },
    setCartItemAddedErrors: (state: CartState) => {
      state.cartItemAddedErrors = undefined;
    },
    clearCartItemAddedErrors: (state: CartState) => {
      state.cartItemAddedErrors = undefined;
    },
    setCouponErrors: (state: CartState, action: PayloadAction<CouponErrorsType[] | undefined, string>) => {
      state.couponErrors = action.payload;
    },
    setCartModalOpenState: (state: CartState, action: PayloadAction<boolean, string>) => {
      state.isModalOpened = action.payload;
    },
    setLoadingCart: (state: CartState, action: PayloadAction<boolean>) => {
      state.isLoadingCart = action.payload;
    },
    setLoadingCoupon: (state: CartState, action: PayloadAction<boolean>) => {
      state.isLoadingCoupon = action.payload;
    },
  },
});

export default cartSlice.reducer;

export const {
  setCart,
  requestCartItemAdded,
  receiveCartItemAdded,
  setCouponErrors,
  clearCartItemAddedErrors,
  setCartModalOpenState,
} = cartSlice.actions;
const { setLoadingCart, setLoadingCoupon } = cartSlice.actions;

export const setupCart =
  (directLinkCode?: string, forceNew?: boolean) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setLoadingCart(true));

    const publicApiKey = publicApiTokenSelector(getState());
    const cartId = storage.getValue(publicApiKey, planneWidgetCartKey);
    const originCode = loadSaleOrigin();
    const [affiliationCode, changed] = loadAffiliationCode();

    if (!directLinkCode && cartId != null && !forceNew) {
      dispatch(loadCart(cartId!, originCode || undefined));
    } else {
      dispatch(obtainNewCart(originCode || undefined, directLinkCode, affiliationCode));
    }

    if (cartId && affiliationCode && changed) {
      dispatch(setCartAffiliation(cartId, affiliationCode));
    }
  };

export const loadCart =
  (cartId?: string, origin?: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    if (cartId == null) {
      const {
        cart: { cart },
      } = state;

      if (cart) {
        cartId = cart.id;
      } else {
        return;
      }
    }

    let response: AxiosResponse<Cart>;
    try {
      response = await getClient(state).carts.getCart(cartId);
    } catch (err) {
      dispatch(obtainNewCart(origin));
      return;
    }

    const { data: cart } = response!;

    dispatch(registerCart(cart));
    dispatch(ensureSaleOrigin(cart, origin));
  };

export const ensureSaleOrigin =
  (cart: Cart, saleOriginCode?: string) => async (_: AppDispatch, getState: () => RootState) => {
    if (saleOriginCode == null || cart.saleOrigin?.code === saleOriginCode) {
      return;
    }

    try {
      await getClient(getState()).carts.setCartSaleOrigin(cart.id, { saleOriginCode });
    } catch (err) {
      console.error('Could not associate sale origin!');

      throw err;
    }
  };

export const obtainNewCart =
  (saleOriginCode?: string, directLinkCode?: string, affiliationCode?: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    let response: AxiosResponse<Cart>;
    affiliationCode = affiliationCode ?? undefined;
    try {
      response = await getClient(getState()).carts.createCart({ directLinkCode, saleOriginCode, affiliationCode });
    } catch (err) {
      dispatch(setLoadingCoupon(false));

      console.error('Could not create cart!');

      throw err;
    } finally {
      dispatch(setLoadingCart(false));
    }

    const { data: cart } = response;
    dispatch(registerCart(cart));
  };

export const registerCart = (cart: Cart) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const publicApiKey = publicApiTokenSelector(getState());

  const { cart: cartWithoutCouponErrors, couponErrors } = extractCouponErrorsFromCartGeneralErrors(cart);

  dispatch(registerCouponErrors(couponErrors));

  dispatch(setCart(cartWithoutCouponErrors));
  storage.setValue(publicApiKey!, planneWidgetCartKey, cart.id);

  dispatch(setLoadingCart(false));
  dispatch(setLoadingCoupon(false));
};

export const clearCartReference = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const publicApiKey = publicApiTokenSelector(getState());
  storage.removeValue(publicApiKey!, planneWidgetCartKey);
};

export const setCartAffiliation =
  (cartId: string, affiliationCode: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { data } = await getClient(getState()).carts.setCartAffiliation(cartId, { affiliationCode });

    dispatch(setCart(data));
  };

export const addToCart =
  (params: AddCartItemRequest | AddProductPackageToCartRequest, widgetId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      cart: { cart },
    } = state;
    if (cart == null) {
      return;
    }
    dispatch(setProductAdderStatus({ id: widgetId, value: 'adding_to_cart' }));
    dispatch(requestCartItemAdded());

    try {
      if ('items' in params && params.items.length > 0) {
        await addProductPackageToCart(state, cart.id, params as AddProductPackageToCartRequest);
      } else {
        await addCartItem(state, cart.id, params as AddCartItemRequest);
      }
    } catch (err) {
      const errors = validationErrors(err);
      if (errors) {
        dispatch(receiveCartItemAdded({ ok: false, errors }));
        return;
      }

      throw err;
    } finally {
      dispatch(setProductAdderStatus({ id: widgetId, value: 'finished_request' }));
    }

    dispatch(receiveCartItemAdded({ ok: true }));
  };

const addCartItem = async (state: RootState, cartId: string, params: AddCartItemRequest) => {
  const [affiliationCode] = loadAffiliationCode();

  if (affiliationCode) {
    params.affiliationCode = affiliationCode;
  }

  await getClient(state).carts.addCartItem(cartId, params);
};

const addProductPackageToCart = async (state: RootState, cartId: string, params: AddProductPackageToCartRequest) => {
  const [affiliationCode] = loadAffiliationCode();

  if (affiliationCode) {
    params.items = params.items.map((item) => ({
      ...item,
      affiliationCode,
    }));
  }

  await getClient(state).carts.addProductPackageToCart(cartId, params);
};

export const removeCartItem = (item: CartItem) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    cart: { cart },
  } = state;
  if (cart == null) {
    return;
  }

  await getClient(state).carts.removeCartItem(cart.id, item.id);
  dispatch(refreshCart());
};

export const removeCartPackage =
  (productPackageUnitId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      cart: { cart },
    } = state;
    if (cart == null) {
      return;
    }

    await getClient(state).carts.removeProductPackageFromCart(cart.id, productPackageUnitId);
    dispatch(refreshCart());
  };

export const refreshCart = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const {
    cart: { cart },
  } = getState();

  if (cart) {
    dispatch(setLoadingCart(true));
    dispatch(loadCart());
  }
};

export const setCartCoupon = (code: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    cart: { cart },
  } = state;
  if (cart == null) {
    return;
  }
  dispatch(setLoadingCoupon(true));
  dispatch(setCouponErrors(undefined));

  let response: AxiosResponse<Cart>;
  try {
    response = await getClient(state).carts.setCartCoupon(cart.id, { code });
  } catch (err) {
    const isValidationError = (err as AxiosError).response?.status === 422;

    const errors = isValidationError
      ? (err as AxiosError<ErrorResponse>).response!.data?.errors?.map((error) => error)
      : undefined;

    if (errors) {
      const errorKeys = errors.map((e) => snakeCase(e.type)) as CouponErrorsType[];

      dispatch(setCouponErrors(errorKeys));
      return;
    }

    throw err;
  } finally {
    dispatch(setLoadingCoupon(false));
  }

  const { data: newCart } = response!;
  dispatch(registerCart(newCart));
};

export const removeCartCoupon = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    cart: { cart },
  } = state;
  if (cart == null) {
    return;
  }

  const { data: newCart } = await getClient(state).carts.removeCartCoupon(cart.id);
  dispatch(registerCart(newCart));
};

export const openCartModal = () => async (dispatch: AppDispatch) => {
  dispatch(setCartModalOpenState(true));
  dispatch(refreshCart());
};

export const closeCartModal = () => async (dispatch: AppDispatch) => {
  dispatch(setCartModalOpenState(false));
};

export const cartStateSelector = (state: RootState) => state.cart;
export const addCartItemAddedSelector = (state: RootState) => ({
  status: state.cart.cartItemAdded,
  errors: state.cart.cartItemAddedErrors,
});
export const completeCartSelector = (state: RootState) => {
  const { cart, isLoadingCart } = state.cart;

  return {
    cart,
    isLoading: isLoadingCart,
    isReady: cart && cart.items.length > 0,
    hasErrors:
      (cart?.generalErrors && cart.generalErrors.length > 0) ||
      cart?.items.some((cartItem) => cartItem?.errors && cartItem?.errors?.length > 0),
  };
};
export const cartSetCouponErrorsSelector = (state: RootState) => state.cart.couponErrors;

export const cartHasPartnershipWithCouponSelector = (state: RootState) => {
  const { cart } = state.cart;

  return cart?.coupon?.partnershipId != null;
};

export const cartCouponSelector = (state: RootState) => state.cart.cart?.coupon;
export const cartGeneralErrorsSelector = (state: RootState) => state.cart.cart?.generalErrors;

export const extractCouponErrorsFromCartGeneralErrors = (cart: Cart) => {
  const couponErrors = cart.generalErrors?.filter(
    (error) => CouponErrorEnum[error.type as keyof typeof CouponErrorEnum],
  );

  if (couponErrors && couponErrors.length) {
    return {
      cart: {
        ...cart,
        generalErrors: cart.generalErrors?.filter(
          (error) => !CouponErrorEnum[error.type as keyof typeof CouponErrorEnum],
        ),
      },
      couponErrors: couponErrors.map((error) => error.type),
    };
  }

  return { cart, couponErrors: [] as CouponErrorsType[] };
};

export const registerCouponErrors = (errors: string[]) => (dispatch: AppDispatch) => {
  const errorKeys = errors.map((error) => snakeCase(error) as CouponErrorsType);

  dispatch(setCouponErrors(errorKeys));
};
